/**
 * Zugang zum Produkt-Projekt.
 *
 * WARUM DIE WERTE IM QUELLTEXT STEHEN UND NICHT IN DER UMGEBUNG:
 * Beide sind oeffentlich. Die Projekt-URL steht in jedem Connector-Befehl, und
 * der Publishable Key ist dafuer gemacht, im Browser zu stehen — nach dem Build
 * steht er ohnehin sichtbar im Bundle. Eine Build-Zeit-Variable wuerde daran
 * nichts aendern, ausser dass die App ohne gesetzte Variable still auf ein
 * leeres Projekt zeigt und das niemand merkt.
 *
 * Ohne Anmeldung kommt der Key an keine Zeile Daten: alle Tabellen haben RLS,
 * und Policies fuer Nutzer gibt es nur auf `accounts` und `api_keys`, jeweils
 * auf die eigene Zeile.
 *
 * DAS PROJEKT: srcblvgafgkdeaaowhfu ist das PRODUKT (Tappla UG). Das
 * Agenturprojekt aglghhbtgfacoavnealk ist es nicht — siehe AGENTS.md im
 * Wurzelverzeichnis, Abschnitt „Zwei Fallen". Wechselt das Projekt, aendert
 * sich genau diese Datei.
 */

import { t } from './sprache';

export const SUPABASE_URL = 'https://srcblvgafgkdeaaowhfu.supabase.co';
/**
 * Supabase Custom Domain desselben Projekts, seit 13.10.2026. Nur der
 * MCP-Endpunkt nutzt sie: die Metadaten des Servers nennen nur noch diese
 * Adresse, und claude.ai verbindet nur, wenn beide übereinstimmen (AGENTS.md,
 * „Edge Functions“). Auth und REST bleiben auf SUPABASE_URL.
 */
export const API_URL = 'https://api.99takes.com';
export const SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_BHcRqiDeJiWmqWR23_pzKQ_Al88UrGT';

/**
 * Cloudflare Turnstile vor jedem öffentlichen Auth-Aufruf (turnstile.tsx,
 * apps/web/AGENTS.md „Turnstile vor der Anmeldung“). Der Sitekey ist
 * öffentlich wie der Publishable Key; das Secret steht nur in Supabase unter
 * Authentication → Attack Protection, nie hier.
 *
 * Ein Sitekey überall, auch auf localhost (#300): Der Dev-Server spricht die
 * Produktion an, und deren Supabase erkennt nur Tokens des echten Widgets an.
 * Dafür stehen localhost und 127.0.0.1 in den Hostnamen des Widgets „99takes“
 * (Cloudflare-Konto Tappla). Cloudflares Test-Sitekey brachte lokal nur Tokens,
 * die Supabase ablehnte.
 */
export const TURNSTILE_SITE_KEY = '0x4AAAAAAFbXxP5VWnwblzg1';

/**
 * Basis fuer die Rueckkehr aus Bestaetigungs- und Wiederherstellungsmails.
 *
 * Zur Laufzeit aus dem Browser, damit dieselbe Datei auf der Vorschau-Domain
 * (…pages.dev), auf run.99takes.com und im lokalen Dev-Server richtig liegt.
 * Jede dieser Adressen muss in Supabase unter URL Configuration → Redirect URLs
 * eingetragen sein, sonst verweigert der Auth-Server die Rueckleitung.
 */
export const redirectTo = (pfad: string): string => new URL(pfad, window.location.origin).toString();

/**
 * Die Adresse, die ein Kunde in seinen Client eintraegt.
 *
 * Aus SUPABASE_URL abgeleitet statt danebengeschrieben: sie stand bis zum
 * 25.09.2026 zweimal im Quelltext, einmal auf der Konto- und einmal auf der
 * Schluesselseite. Zwei Stellen fuer eine Adresse heisst, dass beim naechsten
 * Projektwechsel eine davon stehen bleibt — und ein Kunde, der die alte kopiert,
 * landet im Agenturprojekt. Genau die Verwechslung steht in AGENTS.md unter
 * „Zwei Fallen".
 */
export const MCP_URL = `${API_URL}/functions/v1/mcp`;

/**
 * Was nach „Verbinden“ in claude.ai passiert (Feedback Testnutzer,
 * 15.10.2026): dreimal registrierte claude.ai einen Client, aber das Fenster
 * kam nie bei uns an (kein /oauth/authorize im Auth-Log), und claude.ai
 * meldete „Du hast begonnen … aber nicht abgeschlossen“. Woran es lag,
 * steht in keinem Log; ein blockiertes Fenster ist die naheliegende Ursache,
 * deshalb „vermutlich“. Steht auf dem Rundgang und auf dem Konto.
 */
export const FENSTER_HINWEIS =
  'Nach „Verbinden“ öffnet sich ein Fenster von 99takes. Erscheint es nicht, lässt dein Browser vermutlich keine Pop-ups von claude.ai zu: erlaube sie und klick noch einmal auf „Verbinden“.';
export const FENSTER_HINWEIS_EN =
  'After “Connect”, a 99takes window opens. If it does not appear, your browser probably blocks pop-ups from claude.ai: allow them and click “Connect” again.';
/** Der Hinweis in der Sprache der App (beim Rendern aufrufen). */
export const fensterHinweis = (): string => t(FENSTER_HINWEIS, FENSTER_HINWEIS_EN);

/**
 * Wo in claude.ai die Connectors stehen. Seit Oktober 2026 unter „Anpassungen“,
 * nicht mehr unter „Einstellungen“ (dort steht nur noch ein Verweis; geprüft
 * am 16.10.2026 in Roberts Konto). Die Namen sind die der deutschen und der
 * englischen Oberfläche.
 */
export const CLAUDE_CONNECTORS = 'https://claude.ai/customize/connectors';
export const connectorPfad = (): string => t('Anpassungen → Konnektoren', 'Customize → Connectors');

/**
 * Die Freigabe der Werkzeuge (Feedback Ingo, 16.10.2026: „jedes einzelne
 * fragt nach Erlaubnis"). claude.ai fragt standardmäßig bei jedem Werkzeug
 * einzeln. Unter dem Connector stehen zwei Gruppen, getrennt nach
 * `readOnlyHint` (anzeige.ts), je mit „Immer erlauben“. Was Credits kostet,
 * schützt das nicht auf: den Preis nennt Claude vorher trotzdem.
 */
export const freigabeHinweis = (): string => t(
  `Damit Claude nicht bei jedem Werkzeug einzeln fragt: unter ${connectorPfad()} → 99takes → Tool-Berechtigungen beide Gruppen auf „Immer erlauben“ stellen. Bevor etwas Credits kostet, nennt Claude trotzdem den Preis.`,
  `So Claude does not ask for every tool separately: under ${connectorPfad()} → 99takes → Tool permissions, set both groups to “Always allow”. Claude still names the price before anything costs credits.`,
);

/** Wohin die Bestaetigungsmail fuehrt. */
export const PFAD_BESTAETIGT = '/confirmed';
/** Wohin der Link aus „Passwort vergessen" fuehrt. */
export const PFAD_PASSWORT_NEU = '/password/new';

/**
 * Wer die Auth-Mails verschickt — steht auf „Sieh in dein Postfach", damit
 * jemand weiss, wonach er im Posteingang sucht.
 *
 * Seit 07.10.2026 `m.99takes.com` (Wurzel-AGENTS.md, „Der Versand"), vorher
 * uebergangsweise `noreply@m.blessing.digital`. Wer den SMTP-Absender in
 * Supabase umstellt, aendert diese Zeile mit — sonst sucht ein Kunde nach einer
 * Adresse, von der nichts mehr kommt.
 */
export const MAIL_ABSENDER = { name: '99takes', adresse: 'no-reply@m.99takes.com' };
